import React, { memo, useEffect, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import axios from 'axios';
import { isEmpty, uniq, remove, cloneDeep, pickBy, identity } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { confirmAlert } from 'react-confirm-alert';
import { openNoSignatureModal } from '../../../../actions/user';

import { closeDocumentForm, addOrReplaceDocumentInList } from '../../../../actions/document';
import {
    leaseTerminationReasonOptions,
    NSW_STATE,
    QLD_STATE,
    DELIVERY_TO_AGENT_AND_TENANT,
    DELIVERY_TYPE_EMAIL,
    FORM_SUBMIT_TYPE_SEND,
    CLOSE_MODAL_MESSAGE,
    DOCUMENT_TERMINATION_NOTICE,
    nswTerminationTenancyOptions,
    NswTerminationReason,
    DELIVERY_TYPE_POST,
    DELIVERY_TYPE_IN_PERSON,
    DELIVERY_TYPE_OVER_16,
    DELIVERY_TYPE_LETTERBOX,
    NSW_TERMINATION_REASON_SUPPORTING_DOCUMENT_REQUIREMENTS
} from '../../../../config';

import DocumentTenantPersonGroup from '../../../../components/document/DocumentTenantPersonGroup';
import { FormTextMultiline } from '../../../../components/form/FormText';
import DatePickerInAgencyTimeZone from '../../../../components/form/DatePickerInAgencyTimeZone';
import { SelectField } from '../../../../components/form/FormSelect';
import ModalDialog from '../../../../common/components/ModalDialog';
import * as validators from '../../../../components/Validate';
import { FormTextRegular } from '../../../../components/form/FormText';

import { getIsOpenTerminationNoticeForm } from '../../../../selectors/document/termination';

import { getAgentSignature } from '../../../../selectors/completion';
import { getUserInfo } from '../../../../selectors/user';

import '../../../../sass/documentModalDialog.scss';
import { getCurrentDocument, getDocumentEditMode } from '../../../../selectors/document';
import { parseISO, isValid } from 'date-fns';
import { getAgencyTimezoneDate, getDeliveryDate, isWeekday } from '../../../../utils/dateUtils';
import { isDocumentEditMode, isDocumentReadOnlyMode } from '../../../../utils/generalUtils';
import LocationAndAddressSelection from '../../LocationAndAddressSelection';
import DocumentHeader from '../../DocumentHeader';
import DeliveryTypeComponent from '../../shared/DeliveryTypeComponent';
import TerminationNoticeWithoutLeaseFooter from './TerminationNoticeWithoutLeaseFooter';
import { CloseSharp } from '@flk-mui-icons';
import { isLeaseLocationNSW, isLeaseLocationQLD } from '../../../../utils/agreementUtils';
import QldNoticeReason from './QldNoticeReason';
import { values as lodashValues } from 'lodash';
import Icon, { Icons, IconSize } from '@app/common/components/Icon';
import ReactTooltip from 'react-tooltip';
import { getLabel } from '@app/utils/labelUtils';
import IntegrationUploadStatus from '@app/components/integration/IntegrationUploadStatus.tsx';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { CONSOLE_INTEGRATION_TERMINATION_NOTICE } from '@app/constants/featureFlags.ts';
import useIntegrationUploadDocuments from '@app/hooks/integration/console/useIntegrationUploadDocuments.ts';
import { integration as Integration } from '@app/constants/constants';
import styles from './TerminationNoticeWithoutLeaseForm.module.scss';
import NswNoticeReasons from './NswNoticeReasons';
import NswNoticeDateWarning from './NswNoticeDateWarning';
import Warning from '@app/components/dashboard/warnings/Warning';
import NswNoticeSupportingDocuments from './NswNoticeSupportingDocuments';
import { formatDocumentsForApi } from '@app/utils/formUtils';
import { mapBooleanToYesNo } from '@app/hooks/helpers';
import Note, { NoteType } from '@app/common/components/Note';
import { UploadStatus } from '../../../../constants/constants';
import useButtonLoadingStatus from '@app/hooks/integration/useIntegrationButtonLoadingStatus';

const TERMINATION_API = 'api/document/termination-notice';
const allowedStates = [NSW_STATE, QLD_STATE];

const SCHEMA_VERSION_NSW_MAY_2025_LEGISLATION_UPDATE = 2;

function TerminationNoticeWithoutLeaseForm() {
    const dispatch = useDispatch();
    const formValuesRef = React.useRef(null);

    const isOpenTerminationNoticeForm = useSelector(getIsOpenTerminationNoticeForm);
    const loggedInUser = useSelector(getUserInfo);
    const agentSignature = useSelector(getAgentSignature);
    const currentDocument = useSelector(getCurrentDocument);
    const documentEditMode = useSelector(getDocumentEditMode);

    const { shouldShowUploadingStatus } = useButtonLoadingStatus(currentDocument);

    const [isSendingTerminateLease, setIsSendingTerminateLease] = useState(false);
    const [isSendingTerminateLeaseSuccess, setIsSendingTerminateLeaseSuccess] = useState(false);
    const [isSendingTerminateLeaseFail, setIsSendingTerminateLeaseFail] = useState(false);
    const [isCreatingTerminationNotice, setIsCreatingTerminationNotice] = useState(false);

    const [currentDocumentId, setCurrentDocumentId] = useState(currentDocument ? currentDocument.id : null);
    const [location, setLocation] = useState(null);
    const [locationAndAddressSelected, setLocationAndAddressSelected] = useState(false);
    let [addEmailFieldError, setAddEmailFieldError] = useState('');
    let [update, setUpdate] = useState(false);
    let [formData, setFormData] = useState({});

    const [integrationPropertyId, setIntegrationPropertyId] = useState(null);
    const [integration, setIntegration] = useState(null);
    const [addressError, setAddressError] = useState('');

    const [isDirty, setIsDirty] = useState(false);
    const [isSaveDraft, setIsSaveDraft] = useState(false);

    const isConsoleIntegrationFeatureFlagEnabled = useFeatureFlag(CONSOLE_INTEGRATION_TERMINATION_NOTICE);
    const { handleUploadClick } = useIntegrationUploadDocuments();

    const [localDocuments, setLocalDocuments] = useState([]);
    const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
    const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

    const isOutdatedNswDocument = React.useMemo(
        () =>
            isLeaseLocationNSW(location) &&
            currentDocument?.schemaVersion !== SCHEMA_VERSION_NSW_MAY_2025_LEGISLATION_UPDATE &&
            isDocumentReadOnlyMode(documentEditMode) &&
            (currentDocument?.completion || currentDocument?.pdf),
        [currentDocument?.schemaVersion, documentEditMode, location, currentDocument?.completion, currentDocument?.pdf]
    );

    const handleUpdateLocalDocuments = documents => {
        setLocalDocuments(documents || []);
        setIsDirty(true);
    };

    const uploadDocuments = () => {
        const options = {
            onUploadProgress: progressEvent => {
                const { loaded, total } = progressEvent;
                setDocumentUploadProgress(Math.floor((loaded * 100) / total));
            }
        };
        let documents = formatDocumentsForApi([...localDocuments]);
        return axios.post('api/document/upload-documents', documents, options);
    };

    const getDataWithDocuments = async data => {
        if (localDocuments.length > 0 && data.willUploadDocument === 'yes') {
            setIsUploadingDocuments(true);
            const uploadResponse = await uploadDocuments();
            setIsUploadingDocuments(false);
            data.documents = uploadResponse.data.documents;
        } else if (data.willUploadDocument === 'no') {
            data.documents = [];
        }
        return data;
    };

    const showConsoleIntegration =
        isConsoleIntegrationFeatureFlagEnabled &&
        integration?.active &&
        integration?.type === Integration.INTEGRATION_REAPIT_CONSOLE &&
        currentDocument.status === 'active' &&
        (currentDocument.location === 'NSW' || currentDocument.location === 'QLD');

    const handleFormDirtyChange = form => {
        if (form.getState().dirty && !isDirty) {
            setIsDirty(true);
        }
    };

    function getResetFormData() {
        setLocation(loggedInUser?.agency?.details?.location || null);
        return {
            location: location,
            agentName: loggedInUser.fullName,
            address: '',
            addressDetails: '',
            tenants: [
                {
                    firstName: '',
                    middleName: '',
                    secondName: '',
                    email: ''
                }
            ],
            emailAddresses: []
        };
    }

    useEffect(() => {
        if (currentDocument && isOpenTerminationNoticeForm) {
            window.history.pushState({}, '', `/dashboard/document/${currentDocument.id}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenTerminationNoticeForm]);

    useEffect(() => {
        if (currentDocument && currentDocument.integration && currentDocument.integration.id) {
            setIntegrationPropertyId(currentDocument.integration.id);
            setIntegration(currentDocument.integration);
        } else {
            setIntegrationPropertyId(null);
            setIntegration(null);
        }
        if (currentDocument) {
            setLocation(currentDocument.location);
            setLocationAndAddressSelected(true);
            setCurrentDocumentId(currentDocument.id);
            setLocalDocuments(currentDocument.details.documents || []);
        }
        setFormData(
            currentDocument
                ? {
                      ...currentDocument.terminationWithoutLease,
                      dateOfNotice:
                          currentDocument && currentDocument.terminationWithoutLease.dateOfNotice
                              ? parseISO(currentDocument.terminationWithoutLease.dateOfNotice)
                              : null,
                      saleDate:
                          currentDocument && currentDocument.details.saleDate
                              ? parseISO(currentDocument.details.saleDate)
                              : null,
                      landlordWillResideFromDate:
                          currentDocument && currentDocument.terminationWithoutLease?.landlordWillResideFromDate
                              ? parseISO(currentDocument.terminationWithoutLease.landlordWillResideFromDate)
                              : null,
                      tenantNoLongerEligibleAffordableHousingDate:
                          currentDocument &&
                          currentDocument.terminationWithoutLease?.tenantNoLongerEligibleAffordableHousingDate
                              ? parseISO(
                                    currentDocument.terminationWithoutLease.tenantNoLongerEligibleAffordableHousingDate
                                )
                              : null,
                      tenantNoLongerEligibleTransitionalHousingDate:
                          currentDocument &&
                          currentDocument.terminationWithoutLease?.tenantNoLongerEligibleTransitionalHousingDate
                              ? parseISO(
                                    currentDocument.terminationWithoutLease
                                        .tenantNoLongerEligibleTransitionalHousingDate
                                )
                              : null,
                      employeeCareTakerArrangementEndDate: currentDocument?.terminationWithoutLease
                          ?.employeeCareTakerArrangementEndDate
                          ? parseISO(currentDocument.terminationWithoutLease.employeeCareTakerArrangementEndDate)
                          : null,
                      premisesChangeOfUseDate:
                          currentDocument && currentDocument.terminationWithoutLease?.premisesChangeOfUseDate
                              ? parseISO(currentDocument.terminationWithoutLease.premisesChangeOfUseDate)
                              : null,
                      address: currentDocument.address,
                      tenants: currentDocument.tenant.tenants ? currentDocument.tenant.tenants : [],
                      agentName: currentDocument.agent.fullName,
                      addressDetails: currentDocument.addressDetails,
                      vacantPossessionDate:
                          currentDocument && currentDocument.terminationWithoutLease
                              ? parseISO(currentDocument.terminationWithoutLease.vacantPossessionDate)
                              : null,
                      location: currentDocument.location,
                      emailTo:
                          currentDocument.details.deliveryType === DELIVERY_TYPE_EMAIL
                              ? DELIVERY_TO_AGENT_AND_TENANT
                              : null,
                      reasonForTermination: currentDocument.details.reasonForTermination,
                      disabled:
                          (currentDocument.completion || currentDocument.pdf) &&
                          isDocumentReadOnlyMode(documentEditMode),
                      qldNoticeReason: currentDocument.terminationWithoutLease?.qldNoticeReason || {},
                      willUploadDocument: mapBooleanToYesNo(
                          currentDocument.terminationWithoutLease?.willUploadDocument
                      ),
                      __today: 'Today'
                  }
                : getResetFormData()
        );
        if (!currentDocument) {
            setLocationAndAddressSelected(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenTerminationNoticeForm, currentDocument]);

    const closeModal = () => {
        setFormData(getResetFormData());
        setIntegrationPropertyId(null);
        setIntegration(null);
        setCurrentDocumentId(null);
        setIsDirty(false);
        dispatch(closeDocumentForm());
        if (currentDocument) {
            window.history.pushState(
                {},
                '',
                `/dashboard/documents/${currentDocument.docType}/${currentDocument.status}`
            );
        }
    };

    const handleCloseModal = () => {
        if (isDirty) {
            confirmAlert({
                title: '',
                message: CLOSE_MODAL_MESSAGE,
                buttons: [
                    {
                        label: 'Yes',
                        onClick: () => {
                            const values = formValuesRef.current;
                            saveDraft(values).then(response => {
                                setIsSendingTerminateLease(false);
                                dispatch(addOrReplaceDocumentInList(response.data.termination));
                            });
                            closeModal();
                        }
                    },
                    {
                        label: 'No',
                        onClick: () => {
                            closeModal();
                        }
                    },
                    {
                        className: 'close close-modal',
                        label: <CloseSharp>Close</CloseSharp>,
                        onClick: () => {}
                    }
                ]
            });
        } else {
            closeModal();
        }
    };

    const viewPdf = docId => {
        window.open(`/reader/document/termination-notice/${docId}`, '_blank');
    };

    const previewPdf = values => {
        setIsSendingTerminateLease(true);
        setIsSendingTerminateLeaseSuccess(false);
        setIsSendingTerminateLeaseFail(false);
        saveDraft(values).then(response => {
            viewPdf(response.data.termination.id);
            setIsSendingTerminateLease(false);
            setIsDirty(false);
            dispatch(addOrReplaceDocumentInList(response.data.termination));
            setCurrentDocumentId(response.data.termination.id);
        });
    };

    const submitData = values => {
        setIsSendingTerminateLease(true);
        setIsSendingTerminateLeaseSuccess(false);
        setIsSendingTerminateLeaseFail(false);
        if (values.submitType === FORM_SUBMIT_TYPE_SEND) {
            setIsSaveDraft(false);
            return sendDocument(values);
        } else {
            setIsSaveDraft(true);
            return saveDraft(values).then(response => {
                setIsSendingTerminateLease(false);
                setIsSendingTerminateLeaseSuccess(true);
                dispatch(addOrReplaceDocumentInList(response.data.termination));
                setCurrentDocumentId(response.data.termination.id);
                setIsDirty(false);
            });
        }
    };
    const saveDraft = async values => {
        let data = cloneDeep(values);
        if (data.deliveryType !== DELIVERY_TYPE_EMAIL || data.emailTo !== DELIVERY_TO_AGENT_AND_TENANT) {
            data.emailAddresses = [loggedInUser.email];
            data.ccEmailAddresses = [];
        }
        data.integrationPropertyId = integrationPropertyId;
        try {
            const dataWithDocuments = await getDataWithDocuments(data);
            const result = await axios.post(
                `${TERMINATION_API}${currentDocumentId ? `/${currentDocumentId}/save` : ''}`,
                dataWithDocuments
            );
            setLocalDocuments(result.data.termination.details.documents || []);
            return result;
        } catch (error) {
            setIsUploadingDocuments(false);
            throw error;
        }
    };

    const sendDocument = async values => {
        let data = cloneDeep(values);
        const keyList = [
            'address',
            'deliveryType',
            'reasonForTermination',
            'dateOfNotice',
            'vacantPossessionDate',
            'saleDate',
            'detailsOfBreach',
            'detailsOfGrounds',
            'landlordWillResideFromDate',
            'tenantNoLongerEligibleAffordableHousingDate',
            'tenantNoLongerEligibleTransitionalHousingDate',
            'premisesChangeOfUseDate'
        ];
        keyList.forEach(dataKey => {
            if (!data[dataKey]) {
                data[dataKey] = null;
            }
        });
        if (data.deliveryType !== DELIVERY_TYPE_EMAIL || data.emailTo !== DELIVERY_TO_AGENT_AND_TENANT) {
            data.emailAddresses = [loggedInUser.email];
            data.ccEmailAddresses = [];
        }
        if (validateEmailList(data)) {
            if (!isEmpty(agentSignature)) {
                data.integrationPropertyId = integrationPropertyId;
                try {
                    const dataWithDocuments = await getDataWithDocuments(data);

                    const result = await axios.post(
                        `${TERMINATION_API}${currentDocumentId ? `/${currentDocumentId}/send` : '/send'}`,
                        dataWithDocuments
                    );

                    setLocalDocuments(result.data.termination.details.documents || []);
                    setIsSendingTerminateLease(false);
                    setIsSendingTerminateLeaseSuccess(true);
                    setIsSendingTerminateLeaseFail(false);
                    setIsDirty(false);
                    closeModal(false);
                } catch (error) {
                    setIsUploadingDocuments(false);
                    setIsSendingTerminateLease(false);
                    setIsSendingTerminateLeaseSuccess(false);
                    setIsSendingTerminateLeaseFail(true);
                }
            } else {
                setIsSendingTerminateLease(false);
                dispatch(openNoSignatureModal());
            }
        } else {
            setIsSendingTerminateLease(false);
        }
    };

    const emailAgentAndTenantHandler = (values, setValue) => {
        values.emailTo = DELIVERY_TO_AGENT_AND_TENANT;
        let emailAddresses = [];
        values.tenants.forEach(tenant => {
            if (tenant.email && validateEmail(tenant.email)) {
                emailAddresses.push(tenant.email);
            }
        });
        values.emailAddresses = uniq(emailAddresses);
        values.ccEmailAddresses = [loggedInUser.email];
        // set form data
        setValue('emailAddresses', values.emailAddresses);
        setValue('ccEmailAddresses', values.ccEmailAddresses);
        // update redux store also
        setFormData(values);
    };

    const validateEmail = value => {
        if (isEmpty(value)) {
            return false;
        }

        if (!validators.isCorrectEmail(value)) {
            setAddEmailFieldError('Wrong email format');
            return false;
        }
        setAddEmailFieldError(undefined);
        return true;
    };

    const validateEmailList = values => {
        if (values.deliveryType === DELIVERY_TYPE_EMAIL) {
            if (values.emailAddresses.length === 0) {
                setAddEmailFieldError('Please enter at least one email address');
                return false;
            }
        }
        setAddEmailFieldError('');
        return true;
    };
    const addTenantPerson = values => {
        let data = cloneDeep(values);
        const newTenant = { firstName: '', middleName: '', secondName: '', email: '' };
        if (data.tenants) {
            data.tenants.push(newTenant);
        } else {
            data.tenants = [newTenant];
        }
        setUpdate(!update);
        setFormData(data);
    };
    const removeTenantPerson = (index, values, disabled) => {
        if (!disabled) {
            let data = cloneDeep(values);
            let emailAddresses = remove(data.emailAddresses, function (n) {
                return n !== data.tenants[index].email;
            });
            data.tenants = [...data.tenants.slice(0, index), ...data.tenants.slice(index + 1)];
            data.emailAddresses = emailAddresses;
            setUpdate(!update);
            setFormData(data);
        }
    };
    const handleAddressChange = (setValue, address) => {
        setValue('address', address);
    };
    const setUpdateAddressDetails = (address, details, values) => {
        if (!address) {
            setAddressError('Address is required');
        } else {
            setAddressError('');
        }
        let data = cloneDeep(values);
        data.address = address;
        data.location = location;
        data.addressDetails = details;
        setFormData(data);
        setIntegrationPropertyId(null);
        setIntegration(null);
    };
    const onChangeEmail = (value, index, values, setValue) => {
        // change email list only email to is agentAndTenant
        if (values.emailTo === DELIVERY_TO_AGENT_AND_TENANT) {
            // set changed value to form values
            values.tenants[index].email = value.target.value;
            emailAgentAndTenantHandler(values, setValue);
        }
    };
    const confirmDetails = () => {
        const values = formValuesRef.current;
        values.__today = 'Today';
        setIsCreatingTerminationNotice(true);
        saveDraft(values)
            .then(response => {
                setCurrentDocumentId(response.data.termination.id);
                setIsDirty(false);
                dispatch(addOrReplaceDocumentInList(response.data.termination));
                setLocationAndAddressSelected(true);
            })
            .finally(() => {
                setIsCreatingTerminationNotice(false);
            });
    };
    const setAddressDetails = (address, details, values) => {
        let data = cloneDeep(values);
        data.address = address;
        data.location = location;
        data.addressDetails = details;
        setIntegrationPropertyId(null);
        setIntegration(null);
        setFormData(data);
    };
    const setIntegrationProperty = propertyDetails => {
        if (propertyDetails) {
            setFormData({
                location: location,
                address: propertyDetails.address,
                agentName: loggedInUser.fullName,
                tenants: propertyDetails.tenants
            });
            setIntegrationPropertyId(propertyDetails.id);
            setIntegration(propertyDetails);
        } else {
            setFormData(getResetFormData());
        }
    };

    const getDateOfNotice = values => {
        if (
            isDocumentReadOnlyMode(documentEditMode) &&
            (currentDocument?.completion || currentDocument?.pdf) &&
            currentDocument?.terminationWithoutLease?.dateOfNotice
        ) {
            return new Date(currentDocument.terminationWithoutLease.dateOfNotice);
        } else {
            values.dateOfNotice = getDeliveryDate(values.dateOfNotice, loggedInUser, values.deliveryType);
            return values.dateOfNotice;
        }
    };

    return (
        <React.Fragment>
            <ModalDialog
                title="Notice of termination"
                isOpen={isOpenTerminationNoticeForm}
                closeModal={handleCloseModal}
                shouldCloseOnOverlayClick={false}
                className={`document-centre-modal document-modal-dialog tenant ${
                    locationAndAddressSelected && location ? 'with-header' : 'without-header'
                }`}
                hideCloseButton={isSendingTerminateLease}
                // Quick fix for search results being cut off by modal
                allowOverflow={!locationAndAddressSelected}
            >
                <Form
                    initialValues={formData}
                    onSubmit={values => {
                        submitData(values);
                    }}
                    validate={({
                        vacantPossessionDate,
                        deliveryType,
                        reasonForTermination,
                        detailsOfBreach,
                        breachedClauses,
                        saleDate,
                        detailsOfGrounds,
                        deliveryTypeDetails,
                        dateOfNotice,
                        emailTo,
                        address,
                        tenants,
                        submitType,
                        qldNoticeReason,
                        tenancyType,
                        landlordWillResideFromDate,
                        tenantNoLongerEligibleAffordableHousingDate,
                        tenantNoLongerEligibleTransitionalHousingDate,
                        premisesChangeOfUseDate,
                        willUploadDocument,
                        employeeCareTakerArrangementEndDate
                    }) => {
                        if (submitType === FORM_SUBMIT_TYPE_SEND) {
                            const isSupportDocumentRequired =
                                NSW_TERMINATION_REASON_SUPPORTING_DOCUMENT_REQUIREMENTS[reasonForTermination]?.required;

                            let qldNoticeReasonError;
                            if (isLeaseLocationQLD(location)) {
                                qldNoticeReasonError = lodashValues(qldNoticeReason).some(i => i === true)
                                    ? undefined
                                    : 'At lease one or more reasons for termination is required';
                            }
                            const clientSideErrors = {
                                address: address === null ? 'Address is required' : undefined,
                                vacantPossessionDate:
                                    !vacantPossessionDate || !isValid(vacantPossessionDate)
                                        ? 'Delivery date of vacant possession is required'
                                        : undefined,
                                deliveryType: validators.isEmptyString(deliveryType)
                                    ? 'Delivery type is required'
                                    : undefined,
                                tenancyType:
                                    isLeaseLocationNSW(location) && !tenancyType
                                        ? 'Tenancy type is required'
                                        : undefined,
                                reasonForTermination:
                                    isLeaseLocationNSW(location) && !reasonForTermination
                                        ? 'Reason is required'
                                        : undefined,
                                deliveryTypeDetails:
                                    deliveryType && deliveryType === 'other' && !deliveryTypeDetails
                                        ? 'Details of the delivery type is required'
                                        : undefined,
                                detailsOfBreach:
                                    reasonForTermination &&
                                    reasonForTermination === NswTerminationReason.Breach &&
                                    !detailsOfBreach
                                        ? 'Details of the breach are required'
                                        : undefined,
                                breachedClauses:
                                    reasonForTermination &&
                                    reasonForTermination === NswTerminationReason.Breach &&
                                    !breachedClauses
                                        ? 'Breached clause(s) are required'
                                        : undefined,
                                saleDate:
                                    reasonForTermination &&
                                    reasonForTermination === NswTerminationReason.HasSold &&
                                    (!saleDate || !isValid(saleDate))
                                        ? 'Sale date is required'
                                        : undefined,
                                landlordWillResideFromDate:
                                    reasonForTermination === NswTerminationReason.LandlordResidence &&
                                    (!landlordWillResideFromDate || !isValid(landlordWillResideFromDate))
                                        ? 'Date landlord will reside from is required'
                                        : undefined,
                                tenantNoLongerEligibleAffordableHousingDate:
                                    reasonForTermination === NswTerminationReason.AffordableHousing &&
                                    (!tenantNoLongerEligibleAffordableHousingDate ||
                                        !isValid(tenantNoLongerEligibleAffordableHousingDate))
                                        ? 'Eligibility end date is required'
                                        : undefined,
                                tenantNoLongerEligibleTransitionalHousingDate:
                                    reasonForTermination === NswTerminationReason.TransitionalHousing &&
                                    (!tenantNoLongerEligibleTransitionalHousingDate ||
                                        !isValid(tenantNoLongerEligibleTransitionalHousingDate))
                                        ? 'Eligibility end date is required'
                                        : undefined,
                                premisesChangeOfUseDate:
                                    reasonForTermination === NswTerminationReason.ChangeOfUse &&
                                    (!premisesChangeOfUseDate || !isValid(premisesChangeOfUseDate))
                                        ? 'Change of use date is required'
                                        : undefined,
                                employeeCareTakerArrangementEndDate:
                                    reasonForTermination === NswTerminationReason.EmployeeCaretaker &&
                                    (!employeeCareTakerArrangementEndDate ||
                                        !isValid(employeeCareTakerArrangementEndDate))
                                        ? 'Arrangement end date is required'
                                        : undefined,
                                detailsOfGrounds:
                                    reasonForTermination && reasonForTermination === 'with_grounds' && !detailsOfGrounds
                                        ? 'Details of the grounds is required'
                                        : undefined,
                                dateOfNotice:
                                    (!dateOfNotice || !isValid(dateOfNotice)) && deliveryType !== DELIVERY_TYPE_EMAIL
                                        ? 'Date of notice is required'
                                        : undefined,
                                emailTo:
                                    deliveryType && deliveryType === DELIVERY_TYPE_EMAIL && !emailTo
                                        ? 'Please select the receiver of the termination email'
                                        : undefined,
                                willUploadDocument:
                                    isLeaseLocationNSW(location) && reasonForTermination && !willUploadDocument
                                        ? 'Field is required'
                                        : undefined,
                                supportingDocuments:
                                    isLeaseLocationNSW(location) &&
                                    willUploadDocument === 'yes' &&
                                    isSupportDocumentRequired &&
                                    localDocuments.length === 0
                                        ? 'Supporting documents are required'
                                        : undefined,
                                qldNoticeReason: {
                                    error: qldNoticeReasonError
                                }
                            };
                            let tenantsError = [];
                            tenants &&
                                tenants.forEach(tenant => {
                                    const tenantObject = {
                                        firstName:
                                            validators.isEmptyString(tenant.firstName) ||
                                            !validators.isCorrectNameOrSurname(tenant.firstName)
                                                ? 'First name is required'
                                                : undefined,
                                        secondName:
                                            validators.isEmptyString(tenant.secondName) ||
                                            !validators.isCorrectNameOrSurname(tenant.secondName)
                                                ? 'Last name is required'
                                                : undefined,
                                        email: !validateEmail(tenant.email) ? 'Email is required' : undefined
                                    };
                                    if (!isEmpty(tenantObject)) {
                                        tenantsError.push(tenantObject);
                                    }
                                });
                            clientSideErrors.tenants = isEmpty(tenantsError) ? undefined : tenantsError;

                            // We should clear successfully save message or error message before show validations
                            if (!isEmpty(pickBy(clientSideErrors, identity))) {
                                setIsSendingTerminateLeaseSuccess(false);
                                setIsSendingTerminateLeaseFail(false);
                            }

                            return clientSideErrors;
                        } else {
                            return {};
                        }
                    }}
                >
                    {({ handleSubmit, values, form, errors }) => {
                        // set the values to the ref so that the close modal can access these values
                        formValuesRef.current = values;

                        const isNswSupportingDocumentRequired =
                            isLeaseLocationNSW(location) &&
                            !!values.reasonForTermination &&
                            NSW_TERMINATION_REASON_SUPPORTING_DOCUMENT_REQUIREMENTS[values.reasonForTermination]
                                ?.required;
                        return (
                            <form onSubmit={handleSubmit} noValidate id="create-termination-form">
                                <FormSpy subscription={{ values: true }} onChange={() => handleFormDirtyChange(form)} />
                                {locationAndAddressSelected && location && (
                                    <div className="terminate-lease">
                                        <DocumentHeader
                                            parentSelector="create-termination-form"
                                            confirmDetails={confirmDetails}
                                            address={values.address}
                                            addressError={addressError}
                                            handleAddressChange={address => handleAddressChange(form.change, address)}
                                            setAddressDetails={(address, details) =>
                                                setUpdateAddressDetails(address, details, values)
                                            }
                                            disabled={isDocumentReadOnlyMode(documentEditMode) || integrationPropertyId}
                                            agentName={values.agentName}
                                            integration={integration}
                                            viewOnlyIntegration={true}
                                            doc={currentDocument}
                                        />
                                        <div>
                                            {isOutdatedNswDocument && (
                                                <Note
                                                    type={NoteType.INFO}
                                                    title="This document was created prior to the NSW legislation changes on 19 May 2025."
                                                    className={styles.outdatedDocumentWarning}
                                                >
                                                    Due to changes in the form, only limited information can be
                                                    displayed here. The document remains valid - you can view full
                                                    details by opening the PDF. You can also clone it into the updated
                                                    format to align with the new legislation.
                                                </Note>
                                            )}
                                            <div className="tenants">
                                                {formData &&
                                                    formData.tenants &&
                                                    formData.tenants.map((tenant, index) => (
                                                        <DocumentTenantPersonGroup
                                                            key={index}
                                                            index={index}
                                                            removeTenant={index =>
                                                                removeTenantPerson(index, values, values.disabled)
                                                            }
                                                            showHeader={index === 0}
                                                            onChangeEmail={(value, index) =>
                                                                onChangeEmail(value, index, values, form.change)
                                                            }
                                                            disabled={values.disabled}
                                                        />
                                                    ))}
                                                {isDocumentEditMode(documentEditMode) && (
                                                    <div className="button">
                                                        <button
                                                            className="add-tenant"
                                                            type="button"
                                                            onClick={() => addTenantPerson(values)}
                                                        >
                                                            Add tenant
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {!isOutdatedNswDocument && (
                                                <>
                                                    {isLeaseLocationQLD(location) && (
                                                        <QldNoticeReason form={form} disabled={values.disabled} />
                                                    )}
                                                    {isLeaseLocationNSW(location) && (
                                                        <>
                                                            <SelectField
                                                                label="Tenancy type"
                                                                name="tenancyType"
                                                                required
                                                                options={nswTerminationTenancyOptions}
                                                                disabled={values.disabled}
                                                                dataTest="tenancyType"
                                                            />
                                                            <NswNoticeReasons
                                                                disabled={values.disabled}
                                                                selectedReason={values.reasonForTermination}
                                                                values={values}
                                                                isSupportingDocumentRequired={
                                                                    isNswSupportingDocumentRequired
                                                                }
                                                            />
                                                        </>
                                                    )}
                                                    <div className="calendar">
                                                        <DatePickerInAgencyTimeZone
                                                            label="The tenant will deliver vacant possession of the premises on:"
                                                            name="vacantPossessionDate"
                                                            required
                                                            selected={values.vacantPossessionDate}
                                                            minValue={getAgencyTimezoneDate(new Date(), loggedInUser)}
                                                            filterDate={
                                                                isLeaseLocationQLD(location) ? isWeekday : undefined
                                                            }
                                                            toolTipIcon={
                                                                isLeaseLocationQLD(location) && (
                                                                    <Icon
                                                                        icon={Icons.INFO}
                                                                        size={IconSize.SMALL}
                                                                        data-for="terminationNoticeNoWeekendTooltip"
                                                                        className="icon terminate-lease__info-icon"
                                                                        data-tip={true}
                                                                    />
                                                                )
                                                            }
                                                            disabled={values.disabled}
                                                        />
                                                    </div>
                                                    {isLeaseLocationNSW(location) && (
                                                        <>
                                                            <NswNoticeDateWarning
                                                                tenancyType={values.tenancyType}
                                                                dateOfNotice={
                                                                    values.deliveryType === DELIVERY_TYPE_EMAIL
                                                                        ? new Date()
                                                                        : values.dateOfNotice
                                                                }
                                                                vacantPossessionDate={values.vacantPossessionDate}
                                                                reasonForTermination={values.reasonForTermination}
                                                            />
                                                            {values.reasonForTermination && (
                                                                <NswNoticeSupportingDocuments
                                                                    isSupportingDocumentRequired={
                                                                        isNswSupportingDocumentRequired
                                                                    }
                                                                    isDisabled={values.disabled}
                                                                    willUploadDocument={values.willUploadDocument}
                                                                    handleUpdateDocuments={handleUpdateLocalDocuments}
                                                                    currentDocumentId={currentDocumentId}
                                                                    documents={localDocuments}
                                                                    documentUploadProgress={documentUploadProgress}
                                                                    selectedReason={values.reasonForTermination}
                                                                    isUploadingDocuments={isUploadingDocuments}
                                                                    uploadError={errors.supportingDocuments}
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                    {isLeaseLocationQLD(location) && (
                                                        <ReactTooltip
                                                            place="right"
                                                            effect="solid"
                                                            id="terminationNoticeNoWeekendTooltip"
                                                            globalEventOff="wheel"
                                                        >
                                                            <div>
                                                                <strong>Note</strong>:{' '}
                                                                {getLabel(
                                                                    'terminationNoticeDateNotice',
                                                                    location,
                                                                    DOCUMENT_TERMINATION_NOTICE
                                                                )}
                                                            </div>
                                                        </ReactTooltip>
                                                    )}
                                                    <DeliveryTypeComponent
                                                        emailAgentAndTenantHandler={emailAgentAndTenantHandler}
                                                        addEmailFieldError={addEmailFieldError}
                                                        values={values}
                                                        form={form}
                                                        // Use default options if not NSW
                                                        optionsList={
                                                            isLeaseLocationNSW(location)
                                                                ? [
                                                                      DELIVERY_TYPE_EMAIL,
                                                                      DELIVERY_TYPE_IN_PERSON,
                                                                      DELIVERY_TYPE_POST,
                                                                      DELIVERY_TYPE_OVER_16,
                                                                      DELIVERY_TYPE_LETTERBOX
                                                                  ]
                                                                : undefined
                                                        }
                                                    />
                                                    {
                                                        /*
                                                         * If the delivery type is email change the component to a disabled text box
                                                         * only if values.disabled is false ( meaning the document isn't in edit mode and has been sent so we should
                                                         * display the stored date )
                                                         */

                                                        values.deliveryType === DELIVERY_TYPE_EMAIL &&
                                                        !values.disabled ? (
                                                            <FormTextRegular
                                                                name="__today"
                                                                label="Date of the notice:"
                                                                required
                                                                value="Today"
                                                                disabled={true}
                                                            />
                                                        ) : (
                                                            <div className="calendar">
                                                                <DatePickerInAgencyTimeZone
                                                                    label="Date of the notice:"
                                                                    name="dateOfNotice"
                                                                    required
                                                                    selected={
                                                                        getDateOfNotice(values) || values.dateOfNotice
                                                                    }
                                                                    minValue={getAgencyTimezoneDate(
                                                                        new Date(),
                                                                        loggedInUser
                                                                    )}
                                                                    disabled={
                                                                        values.disabled ||
                                                                        values.deliveryType === DELIVERY_TYPE_EMAIL
                                                                    }
                                                                    popperPlacement="top-start"
                                                                />
                                                            </div>
                                                        )
                                                    }
                                                    {isLeaseLocationNSW(location) &&
                                                        values.deliveryType === DELIVERY_TYPE_POST && (
                                                            <Warning>
                                                                Termination notices sent by post require 7 extra working
                                                                days to be added to the minimum notice period to ensure
                                                                legal compliance.
                                                            </Warning>
                                                        )}
                                                    <br />
                                                </>
                                            )}
                                            {showConsoleIntegration && (
                                                <IntegrationUploadStatus
                                                    className={styles.section}
                                                    uploadStatus={
                                                        shouldShowUploadingStatus
                                                            ? UploadStatus.UPLOADING
                                                            : integration.uploadStatus
                                                    }
                                                    lastSuccessfulUpload={integration.lastSuccessfulUpload}
                                                    integration={integration.type}
                                                    upload={() =>
                                                        handleUploadClick({
                                                            docId: currentDocument.id,
                                                            documentType: DOCUMENT_TERMINATION_NOTICE
                                                        })
                                                    }
                                                />
                                            )}
                                            <TerminationNoticeWithoutLeaseFooter
                                                previewPdf={() => previewPdf(values)}
                                                viewPdf={viewPdf}
                                                form={form}
                                                isSendingTerminateLease={isSendingTerminateLease}
                                                isSendingTerminateLeaseSuccess={isSendingTerminateLeaseSuccess}
                                                isSendingTerminateLeaseFail={isSendingTerminateLeaseFail}
                                                isSaveDraft={isSaveDraft}
                                                location={location}
                                            />
                                        </div>
                                    </div>
                                )}
                                {!locationAndAddressSelected && (
                                    <LocationAndAddressSelection
                                        docType={DOCUMENT_TERMINATION_NOTICE}
                                        location={location}
                                        allowedStates={allowedStates}
                                        setLocation={setLocation}
                                        closeModal={closeModal}
                                        confirmDetails={confirmDetails}
                                        address={values.address}
                                        isCreating={isCreatingTerminationNotice}
                                        handleAddressChange={address => handleAddressChange(form.change, address)}
                                        setAddressDetails={(address, details) =>
                                            setAddressDetails(address, details, values)
                                        }
                                        setIntegrationProperty={property => setIntegrationProperty(property)}
                                    />
                                )}
                            </form>
                        );
                    }}
                </Form>
            </ModalDialog>
        </React.Fragment>
    );
}
export default memo(TerminationNoticeWithoutLeaseForm);
